/**
 * Generate HTML for styling the map.
 *
 * @param {boolean} isLargeDisplay
 * @param {boolean} isMobileDisplay
 * @returns
 */
export function generateMapStyle(isLargeDisplay, isMobileDisplay) {
    if (isLargeDisplay) {
        return 'no-pad'
    } else if (isMobileDisplay) {
        return 'no-pad vh-100'
    } else {
        return 'no-pad d-none'
    }
}

/**
 * Generate HTML for styling forms. 
 * 
 * @param {boolean} isLargeDisplay 
 * @param {boolean} isMobileDisplay 
 * @returns 
 */
export function generateFormStyle(isLargeDisplay, isMobileDisplay) {
    if (isLargeDisplay) {
        return 'no-pad search-col vh-100'
    } else if (isMobileDisplay) {
        return 'no-pad search-col vh-25 overflow-hidden'
    } else {
        return 'no-pad search-col vh-100'
    }
}

/**
 * Parse an input string, formatting it for Google's geocoding.
 * 
 * @param {string} location 
 * @returns 
 */
export function generateAddress(location) {
    // If we have a location, process it.
    if (location) {
        // Case 01: Empty or whitespace only.
        if (location.match(/^\s*$/)) return null
        // Case 02: Numeric entry, assume zip code.
        else if (location.match(/^[0-9]+$/)) return location
        // Case 03: All others.
        else return location + ' WA'
    }

    return null
}

/**
 * Parse a map result input, extracting and returning geographic metadata.
 * 
 * @param {*} result 
 * @returns 
 */
export function generateMapResult(result) {
    let locationResult = {}
    let mapResult = null
    if (result) {
        const point = result.geometry.location.toJSON()
        if (point) {
            locationResult = { ...point }
        }
        const isStableAddress = !!result.types.find((t) => {
            switch (t) {
                case 'airport':
                case 'establishment':
                case 'intersection':
                case 'natural_feature':
                case 'park':
                case 'plus_code':
                case 'postal_code':
                case 'premise':
                case 'street_address':
                case 'subpremise':
                    return true
                default:
                    return false
            }
        })
        mapResult = {
            isPrecise: isStableAddress,
            name: result.formatted_address,
            types: result.types,
            ...locationResult,
        }
    }
    return mapResult
}

/**
 * Create an object to determine search behavior using the provided inputs. 
 * 
 * @param {*} search 
 * @param {*} userLocation 
 * @returns 
 */
export function generateFilters(search, userLocation) {
    // Set the variables.
    const filters = {}
    let refreshSearch = false
    let updatePath = false
    const paramsToCheck = [
        'basic',
        'location',
        'county',
        'city',
        'school_name',
        'wage_max',
        'wage_min',
        'zipcode',
        'program_types',
        'program_levels',
        'eligibility_requirement_filters',
        'minimum_education_requirements',
        'minimum_age',
        'industry_tags',
        'radius',
    ]

    // Special Case: Flag the update for certain basic searches.
    if (
        'basic' in search &&
        'location' in search &&
        search.basic === '' &&
        search.location === ''
    ) {
        refreshSearch = true
        updatePath = true
    }

    // Special Case: Flag the update if autosearch is enabled.
    if (search.autosearch === true) { refreshSearch = true }

    // Special Case: Set the user location data if it exists.
    if (userLocation && userLocation.coords)
        filters['current_location'] = {
            lat: userLocation.coords.latitude,
            lon: userLocation.coords.longitude,
        }

    // Special Case: Set the active program type if it's provided.
    let c = null
    if (search.category) {
        const { category } = search
        switch (category.toLowerCase()) {
            case 'all':
            case 'prep':
            case 'explore':
            case 'launch':
                c = category.toLowerCase()
                break
            default:
                break
        }
        updatePath = true
    }

    // Iterate over the parameters, populating filters as necessary.
    for (const param of paramsToCheck) {
        if (search && search[param]) {
            filters[param] = search[param]
            refreshSearch = true
            updatePath = true
        }
    }

    // Return the filters and bools.
    return {
        filters: filters,
        refreshSearch: refreshSearch,
        updatePath: updatePath,
        category: c,
    }
}

/**
 * Cancel ongoing API calls.
 * 
 * @param {*} cancelToken 
 * @returns 
 */
export function handleCancelToken(cancelToken) {
    const cs = cancelToken
    return () => {
        if (cs && cs.current) {
            cs.current.cancel('Cancelling due to unmount')
        }
    }
}

module.exports = {
    generateMapStyle,
    generateFormStyle,
    generateAddress,
    generateMapResult,
    generateFilters,
    handleCancelToken,
}
